import api from '@/utils/httpClient';
import { RequestDto } from '@oncolens/oncolens-ui/lib/dto/event/request';
import { AxiosError } from 'axios';
import { isEmpty } from 'lodash';
import { useMutation, UseMutationOptions } from 'react-query';
import {
  TreatmentHistoryDto,
  TreatmentHistoryForCreationDto,
} from '../components/NewRequest/Templates/osu/dtos/TreatmentHistory';
import { FormValues } from '../components/NewRequest/Templates/osu/store';

const mapToPayload = (data: FormValues) => {
  const comorbidities = [
    ...(data.submitTo.autoImmuneDisease
      ? [
          {
            description: 'Auto immune disease',
          },
        ]
      : []),
    ...(data.submitTo.uncontrolledDiabetes
      ? [
          {
            description: 'Uncontrolled diabetes',
          },
        ]
      : []),
    ...(data.submitTo.activeInfection
      ? [
          {
            description: 'Active infection',
          },
        ]
      : []),
    ...(data.submitTo.dvtpe
      ? [
          {
            description: 'DVT/PE',
          },
        ]
      : []),
    ...(data.submitTo.lowEjectionFraction
      ? [
          {
            description: 'Low ejection fraction',
          },
        ]
      : []),
    ...(data.submitTo.hepatitis
      ? [
          {
            description: 'Hepatitis',
          },
        ]
      : []),
    ...(data.submitTo.hiv
      ? [
          {
            description: 'HIV',
          },
        ]
      : []),
    ...(data.submitTo.highQt
      ? [
          {
            description: 'High QT',
          },
        ]
      : []),
    ...(data.submitTo.brainMetastasis
      ? [
          {
            description: 'Brain Metastasis',
          },
        ]
      : []),
  ];

  function makeStaging(staging: any) {
    const clinical = '837e5af3-14e9-4325-93a4-67cb6f0da741';
    const pathological = '2b1ea756-5758-4b6b-ab30-2e7315024739';
    const postTherapy = 'b285796c-ea85-4428-bc4a-d00cdd73e12f';
    const fromKeyValue = ([key, value]: any) => ({ key, value });
    return {
      stagingDetails: staging.stagingDetails,
      bclcStage: staging.bclcStage,
      tumorSite: staging.tumorSite,
      stagingRowsAndValues: [
        ...(staging[clinical]
          ? [
              {
                stagingTimingId: clinical,
                stagingTimingName: 'Clinical',
                stagingValues: Object.entries(staging[clinical]).map(fromKeyValue),
              },
            ]
          : []),
        ...(staging[pathological]
          ? [
              {
                stagingTimingId: pathological,
                stagingTimingName: 'Pathological',
                stagingValues: Object.entries(staging[pathological]).map(fromKeyValue),
              },
            ]
          : []),
        ...(staging[postTherapy]
          ? [
              {
                stagingTimingId: postTherapy,
                stagingTimingName: 'Post Therapy',
                stagingValues: Object.entries(staging[postTherapy]).map(fromKeyValue),
              },
            ]
          : []),
      ],
    };
  }

  function treatmentHistoriesToDTO(treatmentHistories?: TreatmentHistoryDto[]) {
    return (
      treatmentHistories?.map(({ id, ...treatmentHistoryData }: TreatmentHistoryDto) => {
        return treatmentHistoryData as TreatmentHistoryForCreationDto;
      }) || []
    );
  }

  function simpleStagingsToDTO(simpleStagings?: any[]) {
    return simpleStagings?.map((simpleStaging) => {
      if (!!simpleStaging.id) {
        return simpleStaging;
      }

      const { id, ...rest } = simpleStaging;

      return { ...rest };
    });
  }

  const diseases = Object.values(data.diseases).map((disease: any) => {
    const newDisease = {
      ...disease,
      episodes: Object.values(disease.episodes).filter((episode: any) => !episode.isTargetEpisode),
    };

    const targetEpisode: any = Object.values(disease.episodes).find(
      (episode: any) => episode.isTargetEpisode
    );

    if (disease.isPrimary) {
      newDisease.chiefComplaint = data.patientHistory.chiefComplaint;
      newDisease.medicalHistory = data.patientHistory.medicalHistory;
      newDisease.familyHistory = data.patientHistory.familyHistory;
      newDisease.socialHistory = data.patientHistory.socialHistory;
      newDisease.miscellaneousCaseDetails = data.patientHistory.miscellaneousCaseDetails;
      newDisease.menopausalStatus = data.patientHistory.menopausalStatus;
      newDisease.breastOvarianCancerNear = data.patientHistory.breastOvarianCancerNear;
    }

    newDisease.targetEpisode = {
      ...targetEpisode,
      simpleStagings: simpleStagingsToDTO(targetEpisode.simpleStagings),
      staging: isEmpty(targetEpisode.staging) ? null : makeStaging(targetEpisode.staging),
      riskFactors: [
        ...(targetEpisode.riskFactors?.map((r: any) => ({
          description: r.description,
          value: r.value,
          comments: r.comments,
        })) ?? []),
        ...(disease.isPrimary ? comorbidities : []),
      ],
    };

    return newDisease;
  });
  return {
    patient: {
      organizationId: data.organizationId,
      sexId: data.patient.sexId,
      age: data.patient.age,
    },
    request: {
      workflowId: data.workflowId,
      organizationId: data.organizationId,
      topicForDiscussion: data.submitTo.reasonsForRequest,
      referringProviderEmail: data.referringProvider.email,
      referringProviderName: data.referringProvider.name,
      referringProviderPhone: data.referringProvider.phone,
      priority: data.submitTo.highPriority ? 'High' : 'Normal',
      assignedPhysicianId: data.assignedPhysicianId,
    },
    diseases,
    panels: data.panels.map((p) => {
      if (!p.panel) {
        return null;
      }
      const { id, biomarkers, ...panelRest } = p.panel;
      return {
        ...panelRest,
        biomarkers: biomarkers.map(({ id, panelId, ...restBiomarker }) => ({ ...restBiomarker })),
      };
    }),
    treatmentHistories: treatmentHistoriesToDTO(data.treatmentHistory),
    eventHistories: [
      {
        eventType: 'Biopsy',
        eventDate: data.patient.dateOfLastBiopsy,
        biopsyIsTissueAvailable: data.patient.isTissueAvailable,
        biopsyCollectionSite: data.patient.biopsyCollectionSite,
      },
      {
        eventType: 'Pathology',
        eventDate: data.patient.pathologyDate,
        status: data.patient.pathologyStatus,
        comments: data.patient.pathologyText,
      },
    ],
  };
};

export const saveAndSubmitRequest = (data: FormValues): Promise<RequestDto> => {
  const mappedData = mapToPayload(data);
  return api
    .post('/api/collaborationrequests/save-and-submit', mappedData)
    .then((response: any) => response.data.request as RequestDto);
};

export const updateSaveAndSubmitRequest = (eventId: string, data: FormValues) => {
  const mappedData = mapToPayload(data);
  return api
    .put(`/api/collaborationrequests/save-and-submit/${eventId}`, mappedData)
    .then((response: any) => response.data.request as RequestDto);
};

interface UseUpdateSaveAndSubmitRequestParams {
  eventId: string;
  data: FormValues;
}

export function useUpdateSaveAndSubmitRequest(
  options?: UseMutationOptions<RequestDto, AxiosError, UseUpdateSaveAndSubmitRequestParams>
) {
  return useMutation(
    async ({ eventId, data }) => updateSaveAndSubmitRequest(eventId, data),
    options
  );
}
